import { z } from 'zod'

// LLM Provider enum
export const LLM_PROVIDER_VALUES = ['openai', 'anthropic', 'google'] as const
export type LLMProviderValue = (typeof LLM_PROVIDER_VALUES)[number]

// LLM API Key schema
export const llmApiKeySchema = z.object({
  id: z.string().uuid(),
  organization_id: z.string().uuid(),
  name: z.string(),
  provider: z.enum(LLM_PROVIDER_VALUES),
  key_preview: z.string(),
  is_default: z.boolean(),
  is_active: z.boolean(),
  last_tested_at: z.string().datetime().nullable(),
  last_test_success: z.boolean().nullable(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
})

export type LLMAPIKey = z.infer<typeof llmApiKeySchema>

// LLM API Key List Response schema
export const llmApiKeyListResponseSchema = z.object({
  api_keys: z.array(llmApiKeySchema),
  total: z.number(),
})

export type LLMAPIKeyListResponse = z.infer<typeof llmApiKeyListResponseSchema>

// LLM API Key Create Response schema
export const llmApiKeyCreateResponseSchema = z.object({
  message: z.string(),
  api_key: llmApiKeySchema,
})

export type LLMAPIKeyCreateResponse = z.infer<typeof llmApiKeyCreateResponseSchema>

// LLM API Key Test Response schema
export const llmApiKeyTestResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  provider: z.string(),
  model_used: z.string().nullable(),
})

export type LLMAPIKeyTestResponse = z.infer<typeof llmApiKeyTestResponseSchema>

// Request schemas for validation
export const llmApiKeyCreateRequestSchema = z.object({
  name: z.string().min(1, 'Nome obrigatorio').max(255, 'Nome muito longo'),
  provider: z.enum(LLM_PROVIDER_VALUES, {
    errorMap: () => ({ message: 'Selecione um provedor' }),
  }),
  api_key: z.string().min(1, 'API key obrigatoria'),
  is_default: z.boolean().default(false),
  // Phase 4 D-20: resubmit with save_anyway=true to bypass auto-test failure.
  // Optional so existing call sites (pre plan 04-04) still type-check.
  save_anyway: z.boolean().optional(),
})

export type LLMAPIKeyCreateRequest = z.infer<typeof llmApiKeyCreateRequestSchema>

export const llmApiKeyUpdateRequestSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  is_default: z.boolean().optional(),
  is_active: z.boolean().optional(),
})

export type LLMAPIKeyUpdateRequest = z.infer<typeof llmApiKeyUpdateRequestSchema>

export const llmApiKeyTestRequestSchema = z.object({
  provider: z.enum(LLM_PROVIDER_VALUES),
  api_key: z.string().min(1, 'API key obrigatoria'),
})

export type LLMAPIKeyTestRequest = z.infer<typeof llmApiKeyTestRequestSchema>

// Provider display information
export const LLM_PROVIDER_INFO: Record<
  LLMProviderValue,
  { label: string; placeholder: string; helpText: string }
> = {
  openai: {
    label: 'OpenAI',
    placeholder: 'sk-...',
    helpText: 'Sua API key da OpenAI. Encontre em platform.openai.com',
  },
  anthropic: {
    label: 'Anthropic',
    placeholder: 'sk-ant-...',
    helpText: 'Sua API key da Anthropic. Encontre em console.anthropic.com',
  },
  google: {
    label: 'Google',
    placeholder: 'AIza...',
    helpText: 'Sua API key do Google AI. Encontre em makersuite.google.com',
  },
}
